import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Download, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  REPORTS_BY_SOURCE,
  UPLOAD_SOURCES,
  type UploadReport,
  type UploadSource,
} from "@/lib/uploadConstants";
import {
  downloadLatestUpload,
  fetchLatestUploads,
  type LatestUploadEntry,
  type LatestUploadsBySource,
} from "@/services/blobApi";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface LatestUploadsTableProps {
  source: UploadSource;
  data: LatestUploadsBySource | undefined;
  downloadingId: string | null;
  onDownload: (entry: LatestUploadEntry) => void;
  highlightReport?: UploadReport | null;
  highlightRowRef?: React.RefObject<HTMLTableRowElement>;
}

function LatestUploadsTable({
  source,
  data,
  downloadingId,
  onDownload,
  highlightReport,
  highlightRowRef,
}: LatestUploadsTableProps) {
  const reports = REPORTS_BY_SOURCE[source];
  const byReport = useMemo(() => {
    const map = new Map<string, LatestUploadEntry>();
    (data?.[source] ?? []).forEach((entry) => map.set(entry.report, entry));
    return map;
  }, [data, source]);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[280px]">Report type</TableHead>
            <TableHead>Latest file</TableHead>
            <TableHead className="w-[180px]">Uploaded at</TableHead>
            <TableHead className="w-[220px]">Uploaded by</TableHead>
            <TableHead className="w-[140px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.map((r) => {
            const entry = byReport.get(r.value);
            const isDownloading = entry?.id === downloadingId;
            const isHighlighted = highlightReport === r.value;
            return (
              <TableRow
                key={r.value}
                ref={isHighlighted ? highlightRowRef : undefined}
                className={cn(
                  isHighlighted &&
                    "bg-primary/10 ring-2 ring-primary/40 transition-colors",
                )}
              >
                <TableCell className="font-medium">{r.label}</TableCell>
                <TableCell>
                  {entry ? (
                    <span className="block max-w-[280px] truncate" title={entry.filename}>
                      {entry.filename}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      No uploads yet
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {entry ? formatDate(entry.uploaded_at) : "—"}
                </TableCell>
                <TableCell
                  className="max-w-[220px] truncate text-xs text-muted-foreground"
                  title={entry?.uploaded_by ?? undefined}
                >
                  {entry?.uploaded_by ?? "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!entry || isDownloading}
                    onClick={() => entry && onDownload(entry)}
                  >
                    {isDownloading ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-1.5 h-4 w-4" />
                    )}
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

interface LatestUploadsTabProps {
  focus?: {
    source: UploadSource;
    report: UploadReport;
    token: number;
  } | null;
}

export function LatestUploadsTab({ focus }: LatestUploadsTabProps = {}) {
  const { toast } = useToast();
  const [source, setSource] = useState<UploadSource>(
    focus?.source ?? UPLOAD_SOURCES[0],
  );
  const [highlightReport, setHighlightReport] = useState<UploadReport | null>(
    focus?.report ?? null,
  );
  const highlightRowRef = useRef<HTMLTableRowElement>(null);
  const lastFocusToken = useRef<number | null>(focus?.token ?? null);

  useEffect(() => {
    if (!focus || focus.token === lastFocusToken.current) return;
    lastFocusToken.current = focus.token;
    setSource(focus.source);
    setHighlightReport(focus.report);
  }, [focus]);

  useEffect(() => {
    if (!highlightReport) return;
    highlightRowRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    const timer = window.setTimeout(() => setHighlightReport(null), 3000);
    return () => window.clearTimeout(timer);
  }, [highlightReport, source]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["latestUploads"],
    queryFn: fetchLatestUploads,
  });

  const downloadMutation = useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      downloadLatestUpload(id, filename),
    onError: (err: Error) => {
      toast({
        title: "Download failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const downloadingId = downloadMutation.isPending
    ? downloadMutation.variables?.id ?? null
    : null;

  const handleDownload = (entry: LatestUploadEntry) => {
    downloadMutation.mutate({ id: entry.id, filename: entry.filename });
  };

  return (
    <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Latest uploaded files
          </h2>
          <p className="text-xs text-muted-foreground">
            Download the most recent file uploaded for each report type, grouped by source.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading latest uploads…
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-10 text-sm text-destructive">
          <AlertCircle className="mr-2 h-4 w-4" />
          {(error as Error)?.message ?? "Failed to load latest uploads"}
        </div>
      ) : (
        <Tabs value={source} onValueChange={(v) => setSource(v as UploadSource)}>
          <TabsList className="mb-4">
            {UPLOAD_SOURCES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {s}
              </TabsTrigger>
            ))}
          </TabsList>

          {UPLOAD_SOURCES.map((s) => (
            <TabsContent key={s} value={s} className="focus-visible:outline-none">
              <LatestUploadsTable
                source={s}
                data={data}
                downloadingId={downloadingId}
                onDownload={handleDownload}
                highlightReport={s === source ? highlightReport : null}
                highlightRowRef={s === source ? highlightRowRef : undefined}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </section>
  );
}
